/**
 * Utilities for drawing and managing wall end handles
 */

import { handleSizing } from './handleSizing';
import { drawHandleDisc } from './grabHandleUtils';
import { apply3DRotationTo2D } from './geometryUtils';
import { wallEndpointsXZ } from './wallDisplacement';

/**
 * The two points a wall's end handles sit on: its CENTRELINE endpoints,
 * `center ± dir · dimensions.x/2`.
 *
 * Deliberately NOT `snapToWall`'s `getWallEndpoints`, which prefers a wall's
 * `polygonCorners`. Those are the wall's ELEVATION OUTLINE on its face — a
 * different thing, offset by half a thickness and, where the pipeline mitred
 * or overlapped an end, longer than `dimensions.x`. On wall F270255B of the
 * 00285D0C corpus scan the two disagree by 12.8 cm at ONE end and 5.7 cm at
 * the other, which is what "one handle is on the endpoint and the other
 * isn't" looks like.
 *
 * The centreline is the definition every part of the edit math uses
 * (`wallFromEndpoints`, `computeStretch`, `wallLengthDelta`), so a handle
 * drawn here is exactly where the edit will act. `start` is the `a` end and
 * `end` is `b`, which is the correspondence `wallLengthDelta` relies on.
 */
export const getWallEndPoints = (wall) => {
  if (!wall?.center || !(wall?.dimensions?.x > 0)) return null;
  const { a, b } = wallEndpointsXZ(wall);
  return { start: a, end: b };
};

/**
 * Draw wall end handles
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} wall - Selected wall
 * @param {number} scale - Current zoom scale
 * @param {string} activeHandle - Currently active handle ('start' or 'end')
 * @param {boolean} isDragging - Whether we're currently dragging
 * @param {boolean} debugMode - Whether to show debug mode handles (circles with A/B) or normal handles (squares)
 */
export const drawWallHandles = (
  ctx,
  wall,
  scale,
  activeHandle = null,
  isDragging = false,
  debugMode = false,
  options = {}
) => {
  const endPoints = getWallEndPoints(wall);
  if (!endPoints) return;

  ctx.save();

  // A finger gets the move handle's disc, ON the endpoint: the wall-end
  // math (`wallLengthDelta`) reads the cursor's absolute reach, so an
  // offset disc would lengthen the wall by the offset on the first move.
  if (options.coarse) {
    const dir = apply3DRotationTo2D(wall.rotation);
    const glyph = { x: dir[0], z: dir[1] };
    drawHandleDisc(ctx, endPoints.start, scale, {
      glyph,
      active: activeHandle === 'start',
    });
    drawHandleDisc(ctx, endPoints.end, scale, {
      glyph,
      active: activeHandle === 'end',
    });
    if (isDragging && activeHandle) {
      ctx.strokeStyle = '#10b981';
      ctx.lineWidth = 3 / scale;
      ctx.setLineDash([8 / scale, 4 / scale]);
      ctx.beginPath();
      ctx.moveTo(endPoints.start.x, endPoints.start.z);
      ctx.lineTo(endPoints.end.x, endPoints.end.z);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.restore();
    return;
  }

  // Handle style
  const handleSizeWorld = handleSizing(options.coarse).visualPx / scale;
  const wallDir = apply3DRotationTo2D(wall.rotation);
  const wallGlyph = { x: wallDir[0], z: wallDir[1] };
  const halfSize = handleSizeWorld / 2;

  // Draw start handle
  const startActive = activeHandle === 'start';
  ctx.fillStyle = startActive ? '#0066ff' : '#ffffff';
  ctx.strokeStyle = '#0066ff';
  ctx.lineWidth = 2 / scale;

  if (debugMode) {
    // Debug mode: Draw circle handle with "A" label for start
    ctx.beginPath();
    ctx.arc(endPoints.start.x, endPoints.start.z, halfSize, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Draw label "A" for start handle
    ctx.fillStyle = startActive ? '#ffffff' : '#0066ff';
    ctx.font = `bold ${14 / scale}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('A', endPoints.start.x, endPoints.start.z);
  } else {
    // Normal mode: a small disc, arrow along the wall
    drawHandleDisc(ctx, endPoints.start, scale, {
      glyph: wallGlyph,
      active: startActive,
      radiusPx: handleSizing(false).visualPx / 2,
      palette: 'mouse',
    });
  }

  // Draw end handle
  const endActive = activeHandle === 'end';
  ctx.fillStyle = endActive ? '#0066ff' : '#ffffff';
  ctx.strokeStyle = '#0066ff';

  if (debugMode) {
    // Debug mode: Draw circle handle with "B" label for end
    ctx.beginPath();
    ctx.arc(endPoints.end.x, endPoints.end.z, halfSize, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Draw label "B" for end handle
    ctx.fillStyle = endActive ? '#ffffff' : '#0066ff';
    ctx.fillText('B', endPoints.end.x, endPoints.end.z);
  } else {
    // Normal mode: a small disc, arrow along the wall
    drawHandleDisc(ctx, endPoints.end, scale, {
      glyph: wallGlyph,
      active: endActive,
      radiusPx: handleSizing(false).visualPx / 2,
      palette: 'mouse',
    });
  }

  // Draw preview line if dragging
  if (isDragging && activeHandle) {
    ctx.strokeStyle = '#10b981'; // Green for preview
    ctx.lineWidth = 3 / scale;
    ctx.setLineDash([8 / scale, 4 / scale]);

    ctx.beginPath();
    ctx.moveTo(endPoints.start.x, endPoints.start.z);
    ctx.lineTo(endPoints.end.x, endPoints.end.z);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Draw wall outline
  if (!isDragging) {
    ctx.strokeStyle = '#0066ff';
    ctx.lineWidth = 1 / scale;
    ctx.setLineDash([5 / scale, 5 / scale]);

    ctx.beginPath();
    ctx.moveTo(endPoints.start.x, endPoints.start.z);
    ctx.lineTo(endPoints.end.x, endPoints.end.z);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  ctx.restore();
};

/**
 * Check if a point hits a wall handle
 * @param {Object} point - {x, z} point in world space
 * @param {Object} wall - Wall being checked
 * @param {number} scale - Current zoom scale
 * @returns {string|null} 'start', 'end', or null
 */
/**
 * Draw a cabinet run's end handles (the terminal edges of a wall-backed run)
 * — the same square the wall handles use, in world coordinates.
 * @param {Array<{end:string, point:{x,z}}>} handles — from runEndHandles
 */
export const drawRunEndHandles = (
  ctx,
  handles,
  scale,
  activeEnd = null,
  options = {}
) => {
  if (!handles?.length) return;
  // The same disc as every other handle: finger palette on touch, the
  // white mouse disc otherwise, with the arrow ALONG the wall — and grey
  // when the end is pinned (see runEndRange). Run ends sit ON the point in
  // both palettes: the drag reads the cursor's absolute reach along the
  // wall, so an offset disc would grow the run by the offset.
  const coarse = !!options.coarse;
  for (const h of handles) {
    const glyph = h.dir ? { x: h.dir.x, z: h.dir.z } : 'move';
    drawHandleDisc(ctx, h.point, scale, {
      glyph,
      active: activeEnd === h.end,
      radiusPx: coarse ? undefined : handleSizing(false).visualPx / 2,
      palette: h.pinned ? 'muted' : coarse ? 'finger' : 'mouse',
    });
  }
};

/**
 * Draw a BAY's two edge handles — the grab points that widen or narrow it.
 *
 * THE SAME DISC as the run's end handles. These were deliberately smaller at
 * first — the two edits are nested, so the bigger target was the bigger change
 * — but that argument needs a reader who can see both at once to compare, and
 * the scope split means nobody ever can: a bay's handles are drawn only in BAY
 * scope and the run's only in RUN scope. All the smaller disc bought was a
 * smaller target (user feedback, 2026-10-05).
 *
 * The hit box never shrank with it, so this changes what you can SEE, not what
 * you can hit — and the two now agree.
 *
 * The glyph points ALONG the wall, like the end handles: the drag reads the
 * cursor's absolute reach along that line, so an offset disc would resize by
 * the offset.
 */
export const drawBayEdgeHandles = (
  ctx,
  handles,
  scale,
  activeSide = null,
  options = {}
) => {
  if (!handles?.length) return;
  const coarse = !!options.coarse;
  for (const h of handles) {
    drawHandleDisc(ctx, h.point, scale, {
      glyph: h.dir ? { x: h.dir.x, z: h.dir.z } : 'move',
      active: activeSide === h.side,
      // Sized exactly as `drawRunEndHandles` does, including deferring to the
      // disc's own finger default on a coarse pointer — and greyed by the same
      // rule, because a bay edge CAN be pinned: a hard bay (a sink base, a
      // scanned tall unit) is sized by what stands in it, so the edge is shown
      // and does not promise a drag.
      radiusPx: coarse ? undefined : handleSizing(false).visualPx / 2,
      palette: h.pinned ? 'muted' : coarse ? 'finger' : 'mouse',
    });
  }
};

/**
 * Draw a free-standing leg's DATUM — the run line every number in the island
 * panel is measured from — and name its two faces **Side A** and **Side B**.
 *
 * Drawn as a dash-dot in the run's own blue: the linetype is the drafting
 * convention for a reference line, and keeping the hue tells the reader it
 * belongs to the same run as the end handles without adding a third meaning
 * to the plan's palette. It is a datum, never a target — nothing hit-tests it.
 *
 * The line is not the island's centreline (see `freeSegmentDatum`), and that
 * is precisely why it is worth drawing: with it hidden, "Island depth",
 * "Side A row" and the knee are three numbers measured from somewhere the
 * user cannot see.
 *
 * **Side A and Side B are deliberately not "cook side" and "seating side".**
 * Side A is
 * whichever face `cookSide` names — but that value is a GUESS on 11 of the
 * corpus's 13 legs (`defaultSidedness` faces the nearest wall-backed run, and
 * falls back to 'right' with no runs at all), and it is measurably wrong on at
 * least one, where the island's own sink sits on the other face. Labelling the
 * faces by position rather than by role lets the panel and the plan agree on
 * WHICH face without either of them asserting what it is for.
 *
 * @param {{start:{x,z}, end:{x,z}, inward:{x,z}, cookSide:string}} datum
 */
export const drawFreeSegmentDatum = (ctx, datum, scale) => {
  if (!datum?.start || !datum?.end) return;
  const { start, end, inward } = datum;
  ctx.save();

  ctx.strokeStyle = '#0066ff';
  ctx.lineWidth = 1.25 / scale;
  // Long-dash / short-dash: a centre-line in every drafting convention, and
  // distinct from the solid run outline and the dashed drag preview.
  ctx.setLineDash([14 / scale, 4 / scale, 3 / scale, 4 / scale]);
  ctx.beginPath();
  ctx.moveTo(start.x, start.z);
  ctx.lineTo(end.x, end.z);
  ctx.stroke();
  ctx.setLineDash([]);

  // End ticks, square to the line, so the datum reads as bounded rather than
  // as an edge of something.
  if (inward) {
    const t = 7 / scale;
    for (const p of [start, end]) {
      ctx.beginPath();
      ctx.moveTo(p.x - inward.x * t, p.z - inward.z * t);
      ctx.lineTo(p.x + inward.x * t, p.z + inward.z * t);
      ctx.stroke();
    }

    // Both faces named, at the midpoint. Two labels rather than one arrow:
    // an arrow says "that way", which is only useful if the reader already
    // knows what is over there — and the panel's fields are Side A and Side B.
    // Side A is the `cookSide` face, where `frontDepth` sits.
    //
    // Spelled out rather than a bare "A" / "B": a lone letter among the plan's
    // dimension strings and room labels reads as noise, and the panel names
    // them in full.
    const mid = { x: (start.x + end.x) / 2, z: (start.z + end.z) / 2 };
    const off = 22 / scale;
    ctx.fillStyle = '#0066ff';
    ctx.font = `600 ${12 / scale}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Upright, per the plan's rotation-free text convention — a rotated
    // label is unreadable on a leg that runs near-vertical on screen.
    ctx.fillText('Side A', mid.x + inward.x * off, mid.z + inward.z * off);
    ctx.fillText('Side B', mid.x - inward.x * off, mid.z - inward.z * off);
  }

  ctx.restore();
};

/**
 * The MOVE handle for a free-standing leg: one disc at the middle of its run
 * line, with the four-way glyph.
 *
 * Arming "Move island" used to change nothing on screen. The plan's own grab
 * handle could not fill the gap — it is drawn only for a coarse pointer, and
 * is deliberately withheld from run-owned objects — so on a mouse there was
 * no affordance at all, and the only thing that started the drag was a press
 * inside the SELECTED object. On a 13-foot island that means the one cabinet
 * you happened to click.
 *
 * Sized like the run-end handles rather than the 20px grab disc, so it clears
 * the Side A / Side B labels sitting 22px either side of the same point.
 */
export const drawFreeSegmentMoveHandle = (ctx, datum, scale, options = {}) => {
  if (!datum?.mid) return;
  const coarse = !!options.coarse;
  drawHandleDisc(ctx, datum.mid, scale, {
    glyph: 'move',
    active: !!options.active,
    radiusPx: coarse ? undefined : handleSizing(false).visualPx / 2,
    palette: coarse ? 'finger' : 'mouse',
  });
};

/** Is `point` on a leg's move handle? */
export const hitTestFreeSegmentMoveHandle = (
  point,
  datum,
  scale,
  options = {}
) => {
  if (!datum?.mid) return false;
  const r = handleSizing(options.coarse).hitPx / 2 / scale;
  return (point.x - datum.mid.x) ** 2 + (point.z - datum.mid.z) ** 2 <= r * r;
};

export const hitTestWallHandles = (point, wall, scale, options = {}) => {
  const endPoints = getWallEndPoints(wall);
  if (!endPoints) return null;

  // hitPx is a diameter; the test below is a radius.
  const hitAreaWorld = handleSizing(options.coarse).hitPx / 2 / scale;
  const hitAreaSquared = hitAreaWorld * hitAreaWorld;

  // Check start handle
  const startDistSq =
    Math.pow(point.x - endPoints.start.x, 2) +
    Math.pow(point.z - endPoints.start.z, 2);

  // Check end handle
  const endDistSq =
    Math.pow(point.x - endPoints.end.x, 2) +
    Math.pow(point.z - endPoints.end.z, 2);

  let hitHandle = null;
  if (startDistSq <= hitAreaSquared) {
    hitHandle = 'start';
  } else if (endDistSq <= hitAreaSquared) {
    hitHandle = 'end';
  }

  return hitHandle;
};

export const getWallHandleCursor = (handleType, wall) => {
  if (!wall || !wall.rotation) return 'move';

  // Get wall direction
  const wallDir = apply3DRotationTo2D(wall.rotation);
  const angle = (Math.atan2(wallDir[1], wallDir[0]) * 180) / Math.PI;

  // Normalize angle to 0-360 range
  const normalizedAngle = ((angle % 360) + 360) % 360;

  // For start handle, point backwards (opposite direction)
  // For end handle, point forwards (wall direction)
  const handleAngle =
    handleType === 'start'
      ? normalizedAngle + 180 // Point opposite to wall direction
      : normalizedAngle; // Point in wall direction

  // Map angle to nearest cardinal/intercardinal direction for cursor
  // CSS resize cursors: n-resize, ne-resize, e-resize, se-resize, s-resize, sw-resize, w-resize, nw-resize
  const cursorAngle = ((handleAngle % 360) + 360) % 360;

  if (cursorAngle >= 337.5 || cursorAngle < 22.5) {
    return 'e-resize'; // East (right)
  } else if (cursorAngle >= 22.5 && cursorAngle < 67.5) {
    return 'se-resize'; // Southeast
  } else if (cursorAngle >= 67.5 && cursorAngle < 112.5) {
    return 's-resize'; // South (down)
  } else if (cursorAngle >= 112.5 && cursorAngle < 157.5) {
    return 'sw-resize'; // Southwest
  } else if (cursorAngle >= 157.5 && cursorAngle < 202.5) {
    return 'w-resize'; // West (left)
  } else if (cursorAngle >= 202.5 && cursorAngle < 247.5) {
    return 'nw-resize'; // Northwest
  } else if (cursorAngle >= 247.5 && cursorAngle < 292.5) {
    return 'n-resize'; // North (up)
  } else if (cursorAngle >= 292.5 && cursorAngle < 337.5) {
    return 'ne-resize'; // Northeast
  }

  return 'move'; // Fallback
};
